import { useTranslation } from 'react-i18next';
import { FormConfig } from '@dxb-web-ui-framework/content-composer/components/FormBuilder/types';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { getFloatNumber } from '@dxb-web-ui-framework/content-composer/components/FormBuilder/formElements/FloatNumberInput';
import { getClientTimezone } from '@dxb-web-ui-framework/common/utils/Date';
import { dateRangeFormatter } from '../../../../content-composer/src/components/Generic/SelectedFiltersChip/ValueFormatters/ValueFormatters';
import { useLanguage } from '../../../../internationalization/src/contexts/Internationalization/hooks';
import { TransactionType } from '../../../../transaction/src/domain/Transaction/entities/TransactionType';
import { getTransactionStatus } from '../../../../transaction/src/components/TransactionStatusChip';
import { useTransactionStatuses, useTransactionTypes } from '../../../../transaction/src/contexts/Parameters/hooks';

export const TYPE_DEFAULT_VALUE = -1;
export const STATUS_DEFAULT_VALUE = -1;

const useActivityLogFiltersConfig = (): FormConfig => {
  const { t } = useTranslation();
  const { cultureName } = useLanguage();
  const timezone = getClientTimezone();
  const dateFormat = t('global.settings.date.format');
  const typeOptions = useTransactionTypes();
  const statusOptions = useTransactionStatuses();

  const getActivityLogType: (
    id: number,
    types?: Nullable<TransactionType[]>,
  ) => {
    description: string;
  } = (id, types) => ({
    description: types?.find(type => type.id === id)?.description ?? '',
  });

  return {
    dataTestId: 'activity-log-filters',
    validationSchema: {
      amount: getFloatNumber({}),
    },
    components: [
      {
        key: 'dateUpdated',
        type: 'datePickerRange',
        name: 'dateUpdated',
        label: t('useActivityLogFiltersConfig.dateUpdated.Label'),
        properties: {
          formatExp: t('global.settings.date.format'),
          localeName: cultureName,
          tz: timezone,
          mb: 3,
          btnLabel: t('useActivityLogFiltersConfig.activityLogDateRangePicker.btnLabel'),
          filters: {
            rangeReference: {
              from: 'fromDate',
              to: 'toDate',
            },
            valueFormatter: (dateRange: { from: string; to: string }) =>
              dateRangeFormatter(dateRange.from, dateRange.to, dateFormat),
          },
        },
      },
      {
        key: 'type',
        type: 'select',
        name: 'type',
        label: t('useActivityLogFiltersConfig.type.label'),
        defaultValue: '',
        options:
          typeOptions?.map(type => ({
            value: type.id,
            label: type.description,
          })) ?? [],
        emptyOption: { value: TYPE_DEFAULT_VALUE, label: 'All' },
        properties: {
          variant: 'filled',
          mb: 3,
          filters: {
            valueFormatter: (typeValue: number) =>
              typeValue === TYPE_DEFAULT_VALUE ? 'All' : getActivityLogType(typeValue, typeOptions).description,
          },
        },
      },
      {
        key: 'status',
        type: 'select',
        name: 'status',
        label: t('useActivityLogFiltersConfig.status.label'),
        defaultValue: '',
        options:
          statusOptions?.map(status => ({
            value: status.id,
            label: status.description,
          })) ?? [],
        emptyOption: { value: STATUS_DEFAULT_VALUE, label: 'All' },
        openOnFocus: true,
        properties: {
          variant: 'filled',
          mb: 3,
          filters: {
            valueFormatter: (statusValue: number) =>
              statusValue === TYPE_DEFAULT_VALUE ? 'All' : getTransactionStatus(statusValue, typeOptions, t).value,
          },
        },
      },
      {
        key: 'account',
        type: 'text',
        name: 'account',
        label: t('useActivityLogFiltersConfig.account.label'),
      },
    ],
  };
};

export { useActivityLogFiltersConfig };
